import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, Loader, CheckCircle, CheckCircle2, XCircle, Shield, Eye } from 'lucide-react';
import { apiClient } from '../../config/api';
import Modal from '../../components/Modal';

const AdminCleanupPage = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedGroupId, setSelectedGroupId] = useState('');
    const [cleaning, setCleaning] = useState(false);
    const [cleanupStatus, setCleanupStatus] = useState(null);
    const [cleanupMessage, setCleanupMessage] = useState('');
    const [showGroupConfirm, setShowGroupConfirm] = useState(false);
    const [showAllConfirm, setShowAllConfirm] = useState(false);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [previewCounts, setPreviewCounts] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    
    // Cleanup options state
    const [cleanupOptions, setCleanupOptions] = useState({
        deleteWorkflows: false,
        deleteContactPlans: false,
        deleteContactMessages: false,
        deleteDealerLocations: false,
        deleteUnlayerTemplates: false,
        deleteMessageCatalog: false,
        deleteDealerLocationBrands: false,
        deleteBrandTemplates: false,
        cascadeDeleteContactPlans: false,
        cascadeDeleteLocationBrands: false
    });

    useEffect(() => {
        loadData();
        checkSuperAdmin();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const groupsResult = await apiClient.get('/groups');
            const sortedGroups = (groupsResult || []).sort((a, b) => a.name.localeCompare(b.name));
            setGroups(sortedGroups);
        } catch (error) {
            console.error('Failed to load groups:', error);
            setCleanupStatus('error');
            setCleanupMessage('Failed to load groups.');
        } finally {
            setLoading(false);
        }
    };

    const checkSuperAdmin = async () => {
        // We'll determine super admin status by trying to access the all-groups cleanup
        // The backend will return 403 if not super admin
        // For now, we'll show the option and let the backend handle authorization
        // In a real implementation, you might want to check user roles from the API
        setIsSuperAdmin(true); // Show the option, backend will enforce permissions
    };

    const handlePreview = async () => {
        if (!selectedGroupId) {
            setCleanupStatus('error');
            setCleanupMessage('Please select a group first.');
            return;
        }

        const hasSelection = Object.values(cleanupOptions).some(value => value === true);
        if (!hasSelection) {
            setCleanupStatus('error');
            setCleanupMessage('Please select at least one data type to preview.');
            return;
        }

        setLoadingPreview(true);
        setPreviewCounts(null);
        setCleanupStatus(null);
        setCleanupMessage('');

        try {
            const result = await apiClient.post(`/admin/cleanup/group/${selectedGroupId}/preview`, cleanupOptions);
            setPreviewCounts(result.counts);
            setCleanupStatus('success');
            setCleanupMessage('Preview generated successfully. Review the counts below before confirming deletion.');
        } catch (error) {
            console.error('Failed to preview cleanup:', error);
            setCleanupStatus('error');
            setCleanupMessage(error.message || 'Failed to generate preview.');
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleCleanupGroup = async () => {
        if (!selectedGroupId) {
            setCleanupStatus('error');
            setCleanupMessage('Please select a group first.');
            return;
        }

        // Check if at least one option is selected
        const hasSelection = Object.values(cleanupOptions).some(value => value === true);
        if (!hasSelection) {
            setCleanupStatus('error');
            setCleanupMessage('Please select at least one data type to delete.');
            return;
        }

        setShowGroupConfirm(false);
        setCleaning(true);
        setCleanupStatus(null);
        setCleanupMessage('');

        try {
            const result = await apiClient.delete(`/admin/cleanup/group/${selectedGroupId}`, cleanupOptions);
            setCleanupStatus('success');
            
            // Build detailed message
            const deletedItems = [];
            if (result.deleted.workflows > 0) {
                deletedItems.push(`${result.deleted.workflows} workflow(s)`);
                if (result.deleted.workflowNodes > 0) deletedItems.push(`${result.deleted.workflowNodes} workflow node(s)`);
                if (result.deleted.workflowConnections > 0) deletedItems.push(`${result.deleted.workflowConnections} workflow connection(s)`);
            }
            if (result.deleted.contactPlans > 0) deletedItems.push(`${result.deleted.contactPlans} contact plan(s)`);
            if (result.deleted.contactMessages > 0) deletedItems.push(`${result.deleted.contactMessages} contact message(s)`);
            if (result.deleted.dealerLocations > 0) deletedItems.push(`${result.deleted.dealerLocations} dealer location(s)`);
            if (result.deleted.unlayerTemplates > 0) deletedItems.push(`${result.deleted.unlayerTemplates} UnLayer template(s)`);
            if (result.deleted.messageCatalog > 0) deletedItems.push(`${result.deleted.messageCatalog} message catalog entry/entries`);
            if (result.deleted.dealerLocationBrands > 0) deletedItems.push(`${result.deleted.dealerLocationBrands} dealer location brand relationship(s)`);
            if (result.deleted.brandTemplates > 0) deletedItems.push(`${result.deleted.brandTemplates} brand template(s)`);
            
            setCleanupMessage(
                `Successfully deleted selected data for group: ${result.groupName}\n\n` +
                `Deleted:\n` +
                (deletedItems.length > 0 ? deletedItems.map(item => `- ${item}`).join('\n') : 'No items deleted (counts were 0)')
            );
        } catch (error) {
            console.error('Failed to cleanup group:', error);
            setCleanupStatus('error');
            setCleanupMessage(error.message || 'Failed to cleanup group data.');
        } finally {
            setCleaning(false);
        }
    };

    const handleCleanupAllGroups = async () => {
        setShowAllConfirm(false);
        setCleaning(true);
        setCleanupStatus(null);
        setCleanupMessage('');

        try {
            const result = await apiClient.delete('/admin/cleanup/all-groups');
            setCleanupStatus('success');
            setCleanupMessage(
                `Successfully deleted all data for ALL groups.\n\n` +
                `Deleted:\n` +
                `- ${result.deleted.workflows || 0} workflow(s)\n` +
                `- ${result.deleted.workflowNodes || 0} workflow node(s)\n` +
                `- ${result.deleted.workflowConnections || 0} workflow connection(s)\n` +
                `- ${result.deleted.contactPlans || 0} contact plan(s)\n` +
                `- ${result.deleted.contactMessages || 0} contact message(s)\n` +
                `- Across ${result.deleted.groups || 0} group(s)\n\n` +
                `⚠️ This operation cleared all workflows, contact plans, and messages across all groups.`
            );
        } catch (error) {
            console.error('Failed to cleanup all groups:', error);
            setCleanupStatus('error');
            if (error.response?.status === 403) {
                setCleanupMessage('Access denied. Super admin privileges required to delete all groups.');
            } else {
                setCleanupMessage(error.message || 'Failed to cleanup all groups data.');
            }
        } finally {
            setCleaning(false);
        }
    };

    const selectedGroup = groups.find(g => String(g.id) === String(selectedGroupId));

    return (
        <div className="max-w-4xl mx-auto space-y-6">
            <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <Trash2 size={32} />
                    Data Cleanup
                </h1>
                <p className="text-gray-600">
                    Clear all contact plans, messages, and workflows for testing or before going live.
                </p>
                <p className="text-sm text-red-600 mt-2 font-medium">
                    ⚠️ Warning: These operations cannot be undone. Use with extreme caution.
                </p>
            </div>

            {/* Cleanup Single Group */}
            <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-4">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">Cleanup Single Group</h2>
                    <p className="text-sm text-gray-600">
                        Select which data types to delete for a specific group. Use cascade options to automatically delete related data.
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Group
                    </label>
                    <div className="relative">
                        <select
                            value={selectedGroupId}
                            onChange={(e) => {
                                setSelectedGroupId(e.target.value);
                                setPreviewCounts(null);
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-red-500"
                            disabled={loading || cleaning}
                        >
                            <option value="">Select a group...</option>
                            {groups.map(group => {
                                const isSynced = group.is_voicebox_synced === true;
                                return (
                                    <option key={group.id} value={group.id}>
                                        {group.name}{!isSynced && ' (Not Synced)'}
                                    </option>
                                );
                            })}
                        </select>
                        {selectedGroupId && (() => {
                            const selectedGroup = groups.find(g => g.id === selectedGroupId);
                            const isSynced = selectedGroup?.is_voicebox_synced === true;
                            return isSynced ? (
                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
                                    <CheckCircle2 size={16} className="text-green-600" />
                                </div>
                            ) : null;
                        })()}
                    </div>
                </div>

                {selectedGroupId && (
                    <div className="space-y-4">
                        {/* Main Data Types */}
                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Data Types to Delete</h3>
                            <div className="space-y-2">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteWorkflows}
                                        onChange={(e) => setCleanupOptions({...cleanupOptions, deleteWorkflows: e.target.checked})}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Workflows (includes workflow nodes and connections)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteContactPlans}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, deleteContactPlans: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Contact Plans</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteContactMessages}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, deleteContactMessages: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Contact Messages</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteDealerLocations}
                                        onChange={(e) => setCleanupOptions({...cleanupOptions, deleteDealerLocations: e.target.checked})}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Dealer Locations</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteUnlayerTemplates}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, deleteUnlayerTemplates: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">VoiceBox UnLayer Templates</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteMessageCatalog}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, deleteMessageCatalog: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Message Catalog</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteDealerLocationBrands}
                                        onChange={(e) => setCleanupOptions({...cleanupOptions, deleteDealerLocationBrands: e.target.checked})}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Dealer Location Brands (relationships)</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.deleteBrandTemplates}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, deleteBrandTemplates: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                    />
                                    <span className="text-sm text-gray-700">Brand Templates</span>
                                </label>
                            </div>
                        </div>

                        {/* Cascade Options */}
                        <div className="pt-4 border-t border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Cascade Delete Options</h3>
                            <p className="text-xs text-gray-600 mb-3">When deleting dealer locations, automatically delete related data:</p>
                            <div className="space-y-2 pl-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.cascadeDeleteContactPlans}
                                        onChange={(e) => {
                                            setCleanupOptions({...cleanupOptions, cascadeDeleteContactPlans: e.target.checked});
                                            setPreviewCounts(null);
                                        }}
                                        disabled={!cleanupOptions.deleteDealerLocations}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className={`text-sm ${cleanupOptions.deleteDealerLocations ? 'text-gray-700' : 'text-gray-400'}`}>
                                        Delete Contact Plans when deleting Dealer Locations
                                    </span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={cleanupOptions.cascadeDeleteLocationBrands}
                                        onChange={(e) => setCleanupOptions({...cleanupOptions, cascadeDeleteLocationBrands: e.target.checked})}
                                        disabled={!cleanupOptions.deleteDealerLocations}
                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    />
                                    <span className={`text-sm ${cleanupOptions.deleteDealerLocations ? 'text-gray-700' : 'text-gray-400'}`}>
                                        Delete Dealer Location Brands when deleting Dealer Locations
                                    </span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview Section */}
                {previewCounts && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-start gap-2">
                            <CheckCircle2 className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
                            <div className="flex-1">
                                <p className="font-semibold text-blue-900 mb-2">Preview - Items to be deleted:</p>
                                <div className="text-sm text-blue-800 space-y-1">
                                    {previewCounts.workflows > 0 && (
                                        <p>• {previewCounts.workflows} workflow(s) {previewCounts.workflowNodes > 0 && `(${previewCounts.workflowNodes} nodes, ${previewCounts.workflowConnections} connections)`}</p>
                                    )}
                                    {previewCounts.contactPlans > 0 && <p>• {previewCounts.contactPlans} contact plan(s)</p>}
                                    {previewCounts.contactMessages > 0 && <p>• {previewCounts.contactMessages} contact message(s)</p>}
                                    {previewCounts.dealerLocations > 0 && <p>• {previewCounts.dealerLocations} dealer location(s)</p>}
                                    {previewCounts.unlayerTemplates > 0 && <p>• {previewCounts.unlayerTemplates} UnLayer template(s)</p>}
                                    {previewCounts.messageCatalog > 0 && <p>• {previewCounts.messageCatalog} message catalog entry/entries</p>}
                                    {previewCounts.dealerLocationBrands > 0 && <p>• {previewCounts.dealerLocationBrands} dealer location brand relationship(s)</p>}
                                    {previewCounts.brandTemplates > 0 && <p>• {previewCounts.brandTemplates} brand template(s)</p>}
                                    {Object.values(previewCounts).every(v => v === 0) && (
                                        <p className="text-blue-600 italic">No items found to delete with the selected options.</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {selectedGroup && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={20} />
                            <div className="text-sm text-amber-800">
                                <p className="font-medium mb-1">You are about to delete selected data for:</p>
                                <p className="font-semibold">{selectedGroup.name}</p>
                                <p className="mt-2 font-medium">This action cannot be undone.</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        onClick={handlePreview}
                        disabled={loading || cleaning || loadingPreview || !selectedGroupId || !Object.values(cleanupOptions).some(v => v === true)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {loadingPreview ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Generating Preview...
                            </>
                        ) : (
                            <>
                                <Eye size={16} />
                                Preview Deletion
                            </>
                        )}
                    </button>
                    <button
                        onClick={() => setShowGroupConfirm(true)}
                        disabled={loading || cleaning || loadingPreview || !selectedGroupId || !Object.values(cleanupOptions).some(v => v === true)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                        {cleaning ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Cleaning up...
                            </>
                        ) : (
                            <>
                                <Trash2 size={16} />
                                Delete Selected Data
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Cleanup All Groups (Super Admin Only) */}
            {isSuperAdmin && (
                <div className="bg-white border-2 border-red-300 rounded-lg p-6 space-y-4">
                    <div className="flex items-start gap-3">
                        <Shield className="text-red-600 flex-shrink-0 mt-1" size={24} />
                        <div className="flex-1">
                            <h2 className="text-xl font-semibold text-gray-900 mb-2">Cleanup ALL Groups</h2>
                            <p className="text-sm text-gray-600 mb-2">
                                Super admin only: Delete all workflows, contact plans, and messages for ALL groups.
                            </p>
                            <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                                <div className="flex items-start gap-2">
                                    <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                                    <div className="text-sm text-red-800">
                                        <p className="font-bold mb-2">⚠️ EXTREME WARNING ⚠️</p>
                                        <p className="mb-2">This will permanently delete:</p>
                                        <ul className="list-disc list-inside space-y-1">
                                            <li>ALL workflows across ALL groups</li>
                                            <li>ALL contact plans across ALL groups</li>
                                            <li>ALL contact messages across ALL groups</li>
                                        </ul>
                                        <p className="mt-3 font-bold">This action cannot be undone and will affect all groups in the system.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <button
                        onClick={() => setShowAllConfirm(true)}
                        disabled={loading || cleaning}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 disabled:bg-gray-300 font-semibold"
                    >
                        {cleaning ? (
                            <>
                                <Loader className="animate-spin" size={16} />
                                Cleaning up all groups...
                            </>
                        ) : (
                            <>
                                <Shield size={16} />
                                Delete All Data for ALL Groups (Super Admin)
                            </>
                        )}
                    </button>
                </div>
            )}

            {/* Status Messages */}
            {cleanupStatus === 'success' && (
                <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-4 flex items-start gap-2">
                    <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <pre className="whitespace-pre-wrap font-mono text-xs">{cleanupMessage}</pre>
                </div>
            )}

            {cleanupStatus === 'error' && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-4 flex items-start gap-2">
                    <XCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <pre className="whitespace-pre-wrap font-mono text-xs">{cleanupMessage}</pre>
                </div>
            )}

            {/* Confirmation Modals */}
            <Modal
                isOpen={showGroupConfirm}
                onClose={() => setShowGroupConfirm(false)}
                title="Confirm Group Cleanup"
                size="md"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-md">
                        <AlertTriangle className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                        <div>
                            <p className="font-semibold text-red-900 mb-2">
                                You are about to delete selected data for: {selectedGroup?.name}
                            </p>
                            <p className="text-sm text-red-800 mb-2">This will permanently delete:</p>
                            {previewCounts ? (
                                <div className="text-sm text-red-800 space-y-1">
                                    {previewCounts.workflows > 0 && (
                                        <p>• {previewCounts.workflows} workflow(s) {previewCounts.workflowNodes > 0 && `(${previewCounts.workflowNodes} nodes, ${previewCounts.workflowConnections} connections)`}</p>
                                    )}
                                    {previewCounts.contactPlans > 0 && <p>• {previewCounts.contactPlans} contact plan(s)</p>}
                                    {previewCounts.contactMessages > 0 && <p>• {previewCounts.contactMessages} contact message(s)</p>}
                                    {previewCounts.dealerLocations > 0 && <p>• {previewCounts.dealerLocations} dealer location(s)</p>}
                                    {previewCounts.unlayerTemplates > 0 && <p>• {previewCounts.unlayerTemplates} UnLayer template(s)</p>}
                                    {previewCounts.messageCatalog > 0 && <p>• {previewCounts.messageCatalog} message catalog entry/entries</p>}
                                    {previewCounts.dealerLocationBrands > 0 && <p>• {previewCounts.dealerLocationBrands} dealer location brand relationship(s)</p>}
                                    {previewCounts.brandTemplates > 0 && <p>• {previewCounts.brandTemplates} brand template(s)</p>}
                                    {Object.values(previewCounts).every(v => v === 0) && (
                                        <p className="text-red-600 italic">No items found to delete with the selected options.</p>
                                    )}
                                </div>
                            ) : (
                                <ul className="text-sm text-red-800 list-disc list-inside space-y-1">
                                    {cleanupOptions.deleteWorkflows && <li>All workflows, workflow nodes, and connections</li>}
                                    {cleanupOptions.deleteContactPlans && <li>All contact plans</li>}
                                    {cleanupOptions.deleteContactMessages && <li>All contact messages</li>}
                                    {cleanupOptions.deleteDealerLocations && <li>All dealer locations</li>}
                                    {cleanupOptions.deleteUnlayerTemplates && <li>All VoiceBox UnLayer templates</li>}
                                    {cleanupOptions.deleteMessageCatalog && <li>All message catalog entries</li>}
                                    {cleanupOptions.deleteDealerLocationBrands && <li>All dealer location brand relationships</li>}
                                    {cleanupOptions.deleteBrandTemplates && <li>All brand templates</li>}
                                    {cleanupOptions.deleteDealerLocations && cleanupOptions.cascadeDeleteContactPlans && (
                                        <li className="text-red-700 font-medium">Contact plans (cascade from dealer locations)</li>
                                    )}
                                    {cleanupOptions.deleteDealerLocations && cleanupOptions.cascadeDeleteLocationBrands && (
                                        <li className="text-red-700 font-medium">Dealer location brands (cascade from dealer locations)</li>
                                    )}
                                </ul>
                            )}
                            <p className="text-sm font-medium text-red-900 mt-3">
                                ⚠️ This action cannot be undone.
                            </p>
                        </div>
                    </div>
                    <p className="text-sm text-gray-600">
                        Are you absolutely sure you want to proceed?
                    </p>
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => setShowGroupConfirm(false)}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleCleanupGroup}
                            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                        >
                            Yes, Delete Selected Data
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showAllConfirm}
                onClose={() => setShowAllConfirm(false)}
                title="Confirm Cleanup ALL Groups"
                size="md"
            >
                <div className="space-y-4">
                    <div className="flex items-start gap-3 p-4 bg-red-50 border-2 border-red-300 rounded-md">
                        <Shield className="text-red-600 flex-shrink-0 mt-0.5" size={24} />
                        <div>
                            <p className="font-bold text-red-900 mb-2 text-lg">
                                ⚠️ EXTREME WARNING ⚠️
                            </p>
                            <p className="text-sm text-red-800 mb-2">
                                You are about to delete ALL data for ALL groups in the system.
                            </p>
                            <p className="text-sm text-red-800 mb-2">This will permanently delete:</p>
                            <ul className="text-sm text-red-800 list-disc list-inside space-y-1">
                                <li>ALL workflows across ALL groups</li>
                                <li>ALL contact plans across ALL groups</li>
                                <li>ALL contact messages across ALL groups</li>
                            </ul>
                            <p className="text-sm font-bold text-red-900 mt-3">
                                This action cannot be undone and will affect the entire system.
                            </p>
                        </div>
                    </div>
                    <p className="text-sm text-gray-600">
                        Type <strong className="font-mono bg-gray-100 px-2 py-1 rounded">DELETE ALL</strong> to confirm:
                    </p>
                    <input
                        type="text"
                        id="confirm-text"
                        placeholder="Type DELETE ALL to confirm"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono"
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && e.target.value === 'DELETE ALL') {
                                handleCleanupAllGroups();
                            }
                        }}
                    />
                    <div className="flex justify-end gap-2 pt-4">
                        <button
                            onClick={() => {
                                setShowAllConfirm(false);
                                document.getElementById('confirm-text').value = '';
                            }}
                            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                const confirmText = document.getElementById('confirm-text').value;
                                if (confirmText === 'DELETE ALL') {
                                    handleCleanupAllGroups();
                                } else {
                                    alert('Please type "DELETE ALL" to confirm');
                                }
                            }}
                            className="px-4 py-2 bg-red-700 text-white rounded-md hover:bg-red-800 font-semibold"
                        >
                            Confirm Delete All
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default AdminCleanupPage;
